import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { QrCode, Smartphone, CalendarDays, Users } from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { adminQrAnalytics } from "@/lib/qr-analytics.functions";
import { QR_KIND_LABELS } from "@/lib/qr-links";

const description = "Scan counts for every QR code we print or post: events, volunteer recruiting codes and the general landing code.";

export const Route = createFileRoute("/_authenticated/t1dwpanel/qr-analytics")({
  head: () => ({
    meta: [
      { title: "QR analytics | Warrior Volunteer" },
      { name: "description", content: description },
      { property: "og:title", content: "QR analytics | Warrior Volunteer" },
      { property: "og:description", content: description },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: QrAnalyticsPage,
});

const RANGES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

function kindLabel(kind: string) {
  return QR_KIND_LABELS[kind as keyof typeof QR_KIND_LABELS] ?? kind;
}

function QrAnalyticsPage() {
  const [days, setDays] = useState(90);
  const fetchAnalytics = useServerFn(adminQrAnalytics);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "qr-scans", days],
    queryFn: () => fetchAnalytics({ data: { days } }),
  });

  const eventScans = data?.byKind.find((row) => row.kind === "event")?.scans ?? 0;
  const volunteerScans = data?.byKind.find((row) => row.kind === "volunteer")?.scans ?? 0;
  const mobileScans = data?.byDevice.find((row) => row.device === "mobile")?.scans ?? 0;

  return (
    <AppShell title="QR analytics" eyebrow="Admin">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((range) => (
            <Button
              key={range.days}
              size="sm"
              variant={days === range.days ? "default" : "outline"}
              onClick={() => setDays(range.days)}
            >
              {range.label}
            </Button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat icon={<QrCode className="size-4" />} label="Total scans" value={data?.total ?? 0} />
          <Stat icon={<CalendarDays className="size-4" />} label="Event code scans" value={eventScans} />
          <Stat icon={<Users className="size-4" />} label="Volunteer code scans" value={volunteerScans} />
          <Stat icon={<Smartphone className="size-4" />} label="Scanned on a phone" value={mobileScans} />
        </div>

        {isLoading ? <p className="t-meta text-muted-foreground">Loading scan data...</p> : null}

        <section className="panel p-4">
          <h2 className="t-h3 mb-3">Scans over time</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.byDay ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <ChartTooltip />
                <Line type="monotone" dataKey="scans" stroke="var(--primary)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="panel p-4">
          <h2 className="t-h3 mb-3">Most scanned codes</h2>
          {(data?.byCode ?? []).length === 0 ? (
            <p className="t-meta text-muted-foreground">
              No scans recorded yet in this window. Scans start counting the next time someone
              scans a code printed from the app.
            </p>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data?.byCode ?? []).slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Bar dataKey="scans" fill="var(--primary)" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <SimpleTable
                head={["Code", "Type", "Scans"]}
                rows={(data?.byCode ?? []).map((row) => [row.label, kindLabel(row.kind), row.scans])}
              />
            </>
          )}
        </section>

        <div className="grid gap-4 lg:grid-cols-2">
          <section className="panel p-4">
            <h2 className="t-h3 mb-3">By code type</h2>
            <SimpleTable
              head={["Type", "Scans"]}
              rows={(data?.byKind ?? []).map((row) => [kindLabel(row.kind), row.scans])}
            />
          </section>
          <section className="panel p-4">
            <h2 className="t-h3 mb-3">By device</h2>
            <SimpleTable
              head={["Device", "Scans"]}
              rows={(data?.byDevice ?? []).map((row) => [row.device, row.scans])}
            />
          </section>
        </div>

        <section className="panel p-4">
          <h2 className="t-h3 mb-3">Recent scans</h2>
          <SimpleTable
            head={["When", "Type", "Code", "Device"]}
            rows={(data?.recent ?? []).map((row) => [
              new Date(row.at).toLocaleString(),
              kindLabel(row.kind),
              row.code,
              row.device,
            ])}
          />
        </section>
      </div>
    </AppShell>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="panel flex items-center gap-3 p-4">
      <span className="text-primary">{icon}</span>
      <div>
        <p className="t-meta text-muted-foreground">{label}</p>
        <p className="t-h2">{value}</p>
      </div>
    </div>
  );
}

function SimpleTable({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  if (rows.length === 0) {
    return <p className="t-meta text-muted-foreground">Nothing here yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground">
            {head.map((cell) => (
              <th key={cell} className="t-meta border-b border-border px-2 py-2 font-medium">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/60 last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-2 py-2 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
